import React, { useContext, useMemo, useState, useCallback, useEffect } from "react";
import { ProfileContext } from "layouts/private/ProfileContext";
import { Card, Button, Modal, message, Spin } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import CreditCardLogo from "../components/CreditCardLogo";
import Payment from "../../../shared/AddPaymentMethod/Payment";
import request from "helper/request";

const PaymentMethodPage = () => {
  const profileCtx = useContext(ProfileContext);
  const profile = useMemo(() => profileCtx?.profile, [profileCtx]);
  const setProfile = useMemo(() => profileCtx?.setProfile, [profileCtx]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openPaymentMethodModal, setOpenPaymentMethodModal] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchPaymentMethods = useCallback(async () => {
    setLoading(true);
    try {
      const methods = await request.get("/payment-method/payment-methods");
      setPaymentMethods(methods || []);
      if (profile && setProfile) {
        setProfile({ ...profile, paymentMethods: methods || [] });
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      messageApi.error("Failed to fetch payment methods");
    } finally {
      setLoading(false);
    }
  }, [profile, setProfile, messageApi]);

  useEffect(() => {
    // Initial fetch only - ignore the ESLint warning as this should only run once
    fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeletePaymentMethod = useCallback(
    async (methodId) => {
      setLoading(true);
      try {
        await request.delete(`/payment-method/${methodId}`);
        messageApi.success("Payment method deleted successfully");
        fetchPaymentMethods();
      } catch (error) {
        console.error("Error deleting payment method:", error);
        messageApi.error("Failed to delete payment method");
      } finally {
        setLoading(false);
        setDeleteModalVisible(false);
        setMethodToDelete(null);
      }
    },
    [fetchPaymentMethods, messageApi]
  );

  const handleSetDefault = useCallback(
    async (methodId) => {
      setLoading(true);
      try {
        await request.post(`/payment-method/set-default/${methodId}`);
        messageApi.success("Default payment method updated");
        fetchPaymentMethods();
      } catch (error) {
        console.error("Error setting default payment method:", error);
        messageApi.error("Failed to update default payment method");
      } finally {
        setLoading(false);
      }
    },
    [fetchPaymentMethods, messageApi]
  );

  const handleManageInStripe = useCallback(async () => {
    // Show browser confirmation dialog
    const confirmed = window.confirm(
      "You will be redirected to Stripe to manage your payment methods.\n\nPlease note: It may take up to 2 minutes for Stripe to show all your subscriptions and payment methods.\n\nDo you want to continue?"
    );

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await request.post("/payment-method/create-portal-session");
      // Open Stripe portal in new tab
      window.open(response.url, "_blank");
    } catch (error) {
      console.error("Error creating Stripe portal session:", error);
      messageApi.error("Failed to open Stripe portal");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  const confirmDelete = (method) => {
    setMethodToDelete(method);
    setDeleteModalVisible(true);
  };

  const renderPaymentMethod = (pm, index) => {
    if (!pm || !pm.id) return null;

    const isCard = pm.type === "card";
    const isBankAccount = pm.type === "us_bank_account";
    const isCashApp = pm.type === "cashapp";
    const isDefault = pm.is_default;

    return (
      <div style={style.cardContainer} key={pm.id}>
        <Card
          title=""
          style={{
            ...style.pmCardStyle,
            ...(isDefault ? style.defaultCardStyle : {})
          }}
          hoverable
          actions={[
            isDefault ? (
              <span key="default" style={{ color: "#1890ff", fontWeight: "bold" }}>
                Default
              </span>
            ) : (
              <Button
                key="default"
                type="link"
                onClick={() => handleSetDefault(pm.id)}
                size="small"
              >
                Set as Default
              </Button>
            ),
            <Button
              key="delete"
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmDelete(pm)}
              size="small"
            >
              Delete
            </Button>
          ]}
        >
          <div style={style.cardContent}>
            {isCard && (
              <>
                <CreditCardLogo brand={pm.brand} type={pm.type} />
                <div>
                  <div style={{ display: "flex" }}>
                    <b>{pm.name}</b>
                    <h4 style={{ color: "#4d4d4d", marginLeft: "8px" }}>{pm.brand}</h4>
                  </div>
                  <div>**** **** **** {pm.last4}</div>
                  <div>
                    Exp {pm.exp_month}/{pm.exp_year}
                  </div>
                </div>
              </>
            )}
            {isBankAccount && (
              <>
                <CreditCardLogo brand={pm.brand} type={pm.type} />
                <div>
                  <div style={{ display: "flex" }}>
                    <b>{pm.name}</b>
                    <h4 style={{ color: "#4d4d4d", marginLeft: "8px" }}>{pm.brand}</h4>
                  </div>
                  <div>{pm.account_type} Account</div>
                  <div>****{pm.last4}</div>
                  {pm.status === "verification_required" && (
                    <div>
                      <div
                        style={{ color: "#f5a623", fontSize: "12px", marginTop: "5px" }}
                      >
                        ⚠️ Verification Required
                      </div>
                      <Button
                        type="link"
                        size="small"
                        onClick={handleManageInStripe}
                        style={{ padding: "0", color: "#f5a623", fontSize: "11px" }}
                      >
                        Verify in Stripe →
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
            {isCashApp && (
              <>
                <CreditCardLogo brand={pm.brand} type={pm.type} />
                <div>
                  <div style={{ display: "flex" }}>
                    <b>{pm.name}</b>
                    <h4 style={{ color: "#4d4d4d", marginLeft: "8px" }}>{pm.brand}</h4>
                  </div>
                  <div>Cash App Pay</div>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className="common-form return-process-form">
      {contextHolder}
      <div
        className="mb-3"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <h2 className="title">Payment Methods</h2>
        <div style={{ display: "flex", gap: "10px" }}>
          <Button type="primary" onClick={() => setOpenPaymentMethodModal(true)}>
            Add Payment Method
          </Button>
          <Button type="default" onClick={handleManageInStripe}>
            Manage in Stripe ↗
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        {!paymentMethods?.length && !loading && <div>No payment methods available.</div>}
        {paymentMethods?.map(renderPaymentMethod)}
      </Spin>

      <Payment
        openPaymentMethodModal={openPaymentMethodModal}
        setOpenPaymentMethodModal={setOpenPaymentMethodModal}
        onSuccess={fetchPaymentMethods}
      />

      <Modal
        title="Delete Payment Method"
        visible={deleteModalVisible}
        onOk={() => handleDeletePaymentMethod(methodToDelete?.id)}
        onCancel={() => {
          setDeleteModalVisible(false);
          setMethodToDelete(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okType="danger"
      >
        <p>Are you sure you want to delete this payment method?</p>
        {methodToDelete && (
          <p>
            <strong>{methodToDelete.name}</strong> -{" "}
            {methodToDelete.type === "card"
              ? `****${methodToDelete.last4}`
              : `${methodToDelete.account_type} ****${methodToDelete.last4}`}
          </p>
        )}
      </Modal>
    </div>
  );
};

const style = {
  pmCardStyle: {
    width: "300px",
    margin: "10px"
  },
  defaultCardStyle: {
    backgroundColor: "rgba(24, 144, 255, 0.1)",
    border: "1px solid rgba(24, 144, 255, 0.3)"
  },
  cardContainer: {
    padding: "10px",
    display: "inline-block"
  },
  cardContent: {
    display: "flex",
    alignItems: "center",
    gap: "15px"
  }
};

export default PaymentMethodPage;
